import React, { useRef, useState } from 'react';
import {
  Upload, FileText, X, CheckCircle, AlertTriangle,
  ChevronDown, ChevronUp, Loader2, Info, Network,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../foundation/altzero_base/base/components/ui/tabs';
import { Alert, AlertDescription } from '../../../foundation/altzero_base/base/components/ui/alert';
import type { NEMParseResult, NEMMeterStream, NEMQualityFlag, TouAggregation, TouByNmi, TouUnresolved } from '../types/nemTypes';
import { TouCalculationCard } from '../components/TouCalculationCard';
import { TariffTab } from '../components/NemAnalysisStepper';

// ─── Quality badge ────────────────────────────────────────────────────────────

const QUALITY_LABELS: Record<NEMQualityFlag, { label: string; color: string }> = {
  A: { label: 'Actual',       color: 'bg-green-100 text-green-800' },
  E: { label: 'Estimated',    color: 'bg-yellow-100 text-yellow-800' },
  S: { label: 'Substituted',  color: 'bg-orange-100 text-orange-800' },
  F: { label: 'Final Sub',    color: 'bg-blue-100 text-blue-800' },
  V: { label: 'Variable',     color: 'bg-purple-100 text-purple-800' },
  N: { label: 'Null',         color: 'bg-gray-100 text-gray-700' },
};

const QualityBadge = ({ flag }: { flag: NEMQualityFlag }) => {
  const cfg = QUALITY_LABELS[flag] ?? { label: flag, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${cfg.color}`}>
      {cfg.label}
    </span>
  );
};

// ─── Stream summary card ──────────────────────────────────────────────────────

const StreamSummary = ({ stream }: { stream: NEMMeterStream }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <Card className="overflow-hidden">
      <CardHeader
        className="py-3 px-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-blue-50 p-2">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-sm text-gray-900">
                {stream.nmi}
                <span className="ml-1.5 text-gray-400 font-normal">({stream.nmiSuffix})</span>
              </p>
              <p className="text-xs text-gray-500">
                {stream.startDate} → {stream.endDate} · {stream.intervalLength} min intervals
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {stream.validationErrors.length > 0 && (
              <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700">
                <AlertTriangle className="h-3 w-3" />
                {stream.validationErrors.length} {stream.validationErrors.length === 1 ? 'error' : 'errors'}
              </span>
            )}
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">
                {stream.totalConsumption.toLocaleString(undefined, { maximumFractionDigits: 2 })} {stream.uom}
              </p>
              <p className="text-xs text-gray-400">{stream.intervalCount} intervals</p>
            </div>
            {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="p-0 border-t">
          {/* Metadata grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-100">
            {[
              { label: 'NMI',             value: stream.nmi },
              { label: 'NMI Suffix',      value: stream.nmiSuffix },
              { label: 'Meter Serial',    value: stream.meterSerial || '—' },
              { label: 'Register ID',     value: stream.registerId || '—' },
              { label: 'Data Stream',     value: stream.dataStreamIdentifier || '—' },
              { label: 'UOM',             value: stream.uom },
              { label: 'Interval Length', value: `${stream.intervalLength} min` },
              { label: 'Quality Flags',   value: stream.qualityFlags.join(', ') || '—' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white px-4 py-3">
                <p className="text-xs text-gray-400">{label}</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">{value}</p>
              </div>
            ))}
          </div>

          {/* Validation errors */}
          {stream.validationErrors.length > 0 && (
            <div className="bg-red-50 border-b px-4 py-3 space-y-1">
              {stream.validationErrors.map((msg, i) => (
                <p key={i} className="text-xs text-red-700 flex items-start gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  {msg}
                </p>
              ))}
            </div>
          )}

          {/* Interval days table */}
          {stream.intervals.length > 0 && (
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 border-b">
                  <tr>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Date</th>
                    <th className="py-2 px-3 text-left font-medium text-gray-500">Quality</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Intervals</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Daily Total ({stream.uom})</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Min</th>
                    <th className="py-2 px-3 text-right font-medium text-gray-500">Max</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stream.intervals.map((day) => {
                    const total = day.values.reduce((s, v) => s + v, 0);
                    const min = Math.min(...day.values);
                    const max = Math.max(...day.values);
                    return (
                      <tr key={day.date} className="hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-700">{day.date}</td>
                        <td className="py-2 px-3">
                          <QualityBadge flag={day.qualityFlag} />
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600">{day.values.length}</td>
                        <td className="py-2 px-3 text-right font-medium text-gray-800">
                          {total.toFixed(3)}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-500">{min.toFixed(3)}</td>
                        <td className="py-2 px-3 text-right text-gray-500">{max.toFixed(3)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ─── File upload zone ─────────────────────────────────────────────────────────

type UploadZoneProps = {
  onFile: (file: File) => void;
  isLoading: boolean;
};

const UploadZone = ({ onFile, isLoading }: UploadZoneProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    // reset so same file can be re-selected
    e.target.value = '';
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !isLoading && inputRef.current?.click()}
      className={`
        flex flex-col items-center justify-center rounded-xl border-2 border-dashed
        py-14 gap-4 transition-all cursor-pointer select-none
        ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'}
        ${isLoading ? 'pointer-events-none opacity-60' : ''}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.txt,.nem,.nem12,.nem13"
        className="hidden"
        onChange={handleFileInput}
      />

      {isLoading ? (
        <>
          <Loader2 className="h-10 w-10 text-blue-500 animate-spin" />
          <p className="text-sm font-medium text-gray-600">Parsing NEM file…</p>
        </>
      ) : (
        <>
          <div className="rounded-full bg-blue-50 p-4">
            <Upload className="h-8 w-8 text-blue-500" />
          </div>
          <div className="text-center">
            <p className="font-medium text-gray-700">Drop NEM12 / NEM13 file here, or click to browse</p>
            <p className="text-sm text-gray-400 mt-1">Supports .csv · .txt · .nem · .nem12 · .nem13</p>
          </div>
          <Button variant="outline" type="button" className="gap-2" onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}>
            <FileText className="h-4 w-4" /> Choose File
          </Button>
        </>
      )}
    </div>
  );
};

// ─── Time of Use tab ─────────────────────────────────────────────────────────

const UNRESOLVED_LABEL: Record<TouUnresolved['reason'], string> = {
  no_site:                   'no matching site',
  missing_retailer_or_state: 'retailer/state missing on site',
  no_tou_rates:              'no TOU rates for this retailer/state',
};

const kwh = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });

const TouNmiCard = ({ entry }: { entry: TouByNmi }) => (
  <Card>
    <CardHeader className="py-3 px-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-semibold text-sm text-gray-900">{entry.nmi}</p>
          <p className="text-xs text-gray-500">
            {entry.retailer} · {entry.state} · {entry.meterCount}{' '}
            {entry.meterCount === 1 ? 'meter' : 'meters'}
          </p>
        </div>
      </div>
    </CardHeader>
    <CardContent className="p-0 border-t">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 border-b">
          <tr>
            <th className="py-2 px-3 text-left font-medium text-gray-500">Suffix</th>
            <th className="py-2 px-3 text-left font-medium text-gray-500">Meter Serial</th>
            <th className="py-2 px-3 text-right font-medium text-gray-500">Peak (kWh)</th>
            <th className="py-2 px-3 text-right font-medium text-gray-500">Shoulder (kWh)</th>
            <th className="py-2 px-3 text-right font-medium text-gray-500">Off Peak (kWh)</th>
            <th className="py-2 px-3 text-right font-medium text-gray-500">Total (kWh)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entry.streams.map((s) => (
            <tr key={`${s.suffix}-${s.meterSerial}`} className="hover:bg-gray-50">
              <td className="py-2 px-3 text-gray-700">{s.suffix}</td>
              <td className="py-2 px-3 text-gray-600">{s.meterSerial || '—'}</td>
              <td className="py-2 px-3 text-right text-gray-800">{kwh(s.peak_kwh)}</td>
              <td className="py-2 px-3 text-right text-gray-800">{kwh(s.shoulder_kwh)}</td>
              <td className="py-2 px-3 text-right text-gray-800">{kwh(s.offpeak_kwh)}</td>
              <td className="py-2 px-3 text-right font-medium text-gray-900">{kwh(s.total_kwh)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent>
    <TouCalculationCard entry={entry} />
  </Card>
);

const TouTab = ({ tou }: { tou: TouAggregation }) => {
  const hasByNmi = tou.byNmi.length > 0;
  const hasUnresolved = tou.unresolved.length > 0;

  if (!hasByNmi && !hasUnresolved) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center py-12 gap-2 text-gray-400">
          <Info className="h-8 w-8" />
          <p>No Time-of-Use breakdown for this file</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {hasByNmi && tou.byNmi.map((entry) => (
        <TouNmiCard key={entry.nmi} entry={entry} />
      ))}

      {hasUnresolved && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-amber-600">Unresolved NMIs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {tou.unresolved.map((u) => (
              <div key={u.nmi} className="flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span className="font-medium">{u.nmi}</span>
                <span className="text-amber-600">— {UNRESOLVED_LABEL[u.reason]}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const NEMValidatorPage = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<NEMParseResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setSelectedFile(file);
    setResult(null);
    setError(null);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('nemFile', file);

      const response = await fetch('/api/bill-validation/nem/upload', {
        method: 'POST',
        body: formData,
      });

      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.error || 'Server returned an error');
      }

      setResult(json.data as NEMParseResult);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to parse NEM file');
    } finally {
      setIsLoading(false);
    }
  };

  const reset = () => {
    setSelectedFile(null);
    setResult(null);
    setError(null);
  };

  const totalKwh = result?.meterStreams.reduce((s, m) => s + m.totalConsumption, 0) ?? 0;
  const uniqueNMIs = new Set(result?.meterStreams.map((m) => m.nmi)).size;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Page title */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">NEM File Validator</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Upload a NEM12 or NEM13 file to extract and validate interval meter data
          </p>
        </div>
        {(selectedFile || result) && (
          <Button variant="outline" size="sm" onClick={reset} className="gap-1.5">
            <X className="h-3.5 w-3.5" /> Clear
          </Button>
        )}
      </div>

      {/* Upload zone — shown when no result yet */}
      {!result && (
        <Card>
          <CardContent className="pt-6">
            <UploadZone onFile={handleFile} isLoading={isLoading} />

            {selectedFile && !isLoading && !error && (
              <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
                <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                <span className="font-medium">{selectedFile.name}</span>
                <span className="text-gray-400">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
              </div>
            )}

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {result && (
        <Tabs defaultValue="streams">
          <div className="flex items-center justify-between mb-3">
            <TabsList>
              <TabsTrigger value="streams">Meter Streams ({result.meterStreams.length})</TabsTrigger>
              {result.tou && (
                <TabsTrigger value="tou">Retailer Usage (TOU)</TabsTrigger>
              )}
              {(result.validationRuns?.length || result.unmatchedNmis?.length) ? (
                <TabsTrigger value="tariff">Tariff Structure ({result.validationRuns?.length ?? 0})</TabsTrigger>
              ) : null}
              <TabsTrigger value="meta">File Metadata</TabsTrigger>
              {result.parseErrors.length > 0 && (
                <TabsTrigger value="errors" className="text-red-600">
                  Errors ({result.parseErrors.length})
                </TabsTrigger>
              )}
            </TabsList>

            {/* re-upload */}
            <Button variant="outline" size="sm" onClick={reset} className="gap-1.5">
              <Upload className="h-3.5 w-3.5" /> Upload Another
            </Button>
          </div>

          {/* Summary stat bar */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {[
              { label: 'File Type',     value: result.fileType },
              { label: 'Valid',         value: result.isValid ? '✓ Yes' : '✗ No', highlight: !result.isValid },
              { label: 'Unique NMIs',   value: uniqueNMIs },
              { label: 'Meter Streams', value: result.meterStreams.length },
              { label: 'Total kWh',     value: totalKwh.toLocaleString(undefined, { maximumFractionDigits: 2 }) },
            ].map(({ label, value, highlight }) => (
              <Card key={label}>
                <CardContent className="py-3 px-4">
                  <p className="text-xs text-gray-400">{label}</p>
                  <p className={`text-lg font-semibold mt-0.5 ${highlight ? 'text-red-600' : 'text-gray-900'}`}>{value}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Streams tab */}
          <TabsContent value="streams" className="space-y-3 mt-0">
            {result.meterStreams.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center py-12 gap-2 text-gray-400">
                  <Info className="h-8 w-8" />
                  <p>No meter streams found in this file</p>
                </CardContent>
              </Card>
            ) : (
              result.meterStreams.map((stream, i) => (
                <StreamSummary key={`${stream.nmi}-${stream.nmiSuffix}-${i}`} stream={stream} />
              ))
            )}
          </TabsContent>

          {/* Time of Use tab */}
          {result.tou && (
            <TabsContent value="tou" className="space-y-3 mt-0">
              <TouTab tou={result.tou} />
            </TabsContent>
          )}

          {(result.validationRuns?.length || result.unmatchedNmis?.length) ? (
            <TabsContent value="tariff" className="space-y-3 mt-0">
              {result.unmatchedNmis && result.unmatchedNmis.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm text-red-600">Unmatched NMIs</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {result.unmatchedNmis.map(u => (
                      <div key={u.nmi} className="flex items-center gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
                        <AlertTriangle className="h-4 w-4 shrink-0" />
                        <span className="font-medium">{u.nmi}</span>
                        <span className="text-red-600">— {u.reason}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
              {result.validationRuns?.map(run => (
                <div key={run.site_id} className="space-y-2">
                  <p className="text-xs text-gray-500">
                    {run.nmi} · site {run.site_id.slice(0, 8)}… · {run.period_from} → {run.period_to}
                  </p>
                  <TariffTab
                    summary={run.dist}
                    siteId={run.site_id}
                    periodFrom={run.period_from}
                    periodTo={run.period_to}
                  />
                </div>
              ))}
              {result.validationRuns && result.validationRuns.length === 0 && result.unmatchedNmis && result.unmatchedNmis.length === 0 && (
                <Card>
                  <CardContent className="flex flex-col items-center py-12 gap-2 text-gray-400">
                    <Network className="h-8 w-8" />
                    <p>No tariff runs for this file</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          ) : null}

          {/* Metadata tab */}
          <TabsContent value="meta" className="mt-0">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm text-gray-600">File Header Information</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                  {[
                    { term: 'File Type',        desc: result.fileType },
                    { term: 'Version Header',   desc: result.versionHeader || '—' },
                    { term: 'Created At',       desc: result.createdAt || '—' },
                    { term: 'Participant From', desc: result.participantFrom || '—' },
                    { term: 'Participant To',   desc: result.participantTo || '—' },
                    { term: 'Filename',         desc: selectedFile?.name || '—' },
                    { term: 'File Size',        desc: selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB` : '—' },
                    { term: 'Parse Errors',     desc: String(result.parseErrors.length) },
                  ].map(({ term, desc }) => (
                    <div key={term}>
                      <dt className="text-xs text-gray-400">{term}</dt>
                      <dd className="text-sm font-medium text-gray-800 mt-0.5">{desc}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Errors tab */}
          {result.parseErrors.length > 0 && (
            <TabsContent value="errors" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm text-red-600">Parse Warnings / Errors</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {result.parseErrors.map((msg, i) => (
                    <div key={i} className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <span>{msg}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* Format hint */}
      {!result && !isLoading && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <strong>NEM12</strong> files contain interval meter data (5, 15 or 30 minute intervals).{' '}
            <strong>NEM13</strong> files contain accumulation meter reads.
            The parser extracts NMI, meter serial, UOM, interval length, date range, and daily consumption totals.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
